import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ResourceType, dashboardService } from "@/features/Dashboard";

// Query Keys
export const RESOURCE_DETAILS_QUERY_KEYS = {
  all: ["resourceDetails"] as const,
  types: () => [...RESOURCE_DETAILS_QUERY_KEYS.all, "types"] as const,
  technologies: () =>
    [...RESOURCE_DETAILS_QUERY_KEYS.all, "technologies"] as const,
  combined: () => [...RESOURCE_DETAILS_QUERY_KEYS.all, "combined"] as const,
};

// Hook para tipos de recursos
export function useResourceTypesQuery() {
  const query = useQuery({
    queryKey: RESOURCE_DETAILS_QUERY_KEYS.types(),
    queryFn: () => dashboardService.getResourceTypes(),
    staleTime: 10 * 60 * 1000, // 10 minutos (dados raramente mudam)
    gcTime: 30 * 60 * 1000, // 30 minutos
    refetchOnWindowFocus: false,
    retry: 3,
  });

  return {
    types: query.data || {},
    loading: query.isLoading,
    error: query.error?.message || null,
    refetch: query.refetch,
  };
}

// Hook para tecnologias
export function useTechnologiesQuery() {
  const query = useQuery({
    queryKey: RESOURCE_DETAILS_QUERY_KEYS.technologies(),
    queryFn: () => dashboardService.getTechnologies(),
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
    refetchOnWindowFocus: false,
    retry: 3,
  });

  return {
    technologies: query.data || {},
    loading: query.isLoading,
    error: query.error?.message || null,
    refetch: query.refetch,
  };
}

// Hook combinado (mais eficiente para uso conjunto)
export function useResourceDetailsQuery() {
  const typesQuery = useResourceTypesQuery();
  const technologiesQuery = useTechnologiesQuery();

  const loading = typesQuery.loading || technologiesQuery.loading;
  const error = typesQuery.error || technologiesQuery.error;

  const getTypeName = (typeId: string): string => {
    return typesQuery.types[typeId]?.name || typeId;
  };

  const getTechnologyName = (technologyId: string): string => {
    return technologiesQuery.technologies[technologyId]?.name || technologyId;
  };

  return {
    types: typesQuery.types,
    technologies: technologiesQuery.technologies,
    getTypeName,
    getTechnologyName,
    loading,
    error,
    refetch: () => {
      typesQuery.refetch();
      technologiesQuery.refetch();
    },
  };
}

// Hook para invalidar cache de resource details
export function useInvalidateResourceDetails() {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: RESOURCE_DETAILS_QUERY_KEYS.all,
    });
  };

  const invalidateTypes = () => {
    queryClient.invalidateQueries({
      queryKey: RESOURCE_DETAILS_QUERY_KEYS.types(),
    });
  };

  const invalidateTechnologies = () => {
    queryClient.invalidateQueries({
      queryKey: RESOURCE_DETAILS_QUERY_KEYS.technologies(),
    });
  };

  return {
    invalidateAll,
    invalidateTypes,
    invalidateTechnologies,
  };
}
